import React, { useState } from 'react'
import styled from 'styled-components'
import {
  PageHeader,
  Card,
  Button,
  FilterContainer,
  RangePicker,
  Banner,
  Dropdown,
  Chevron,
  Spinner
} from '@lmc/ui-kit'
import { LineChart } from '@lmc/ui-kit'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Home } from '@lmc/ts-common'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import dayjs from 'dayjs'
import { colors } from '@lmc/common/constants'
import PageWrapper from '../componentsV2/PageWrapper'
import { DownloadOutlined } from '@ant-design/icons'
import ExternalLink from 'components/icons/ExternalLink'
import { StockRecordsTable } from './StockRecordsTable/StockRecordsTable'
import StockCheckModal from '../modalsV2/StockCheckModal'
import DeliveryModal from '../modalsV2/DeliveryModal'
import DisposalModal, { DisposalData } from '../modalsV2/DisposalModal'
import { StockRecordDrawer } from './StockRecordDrawer/StockRecordDrawer'
import { useResidentMedications } from '../hooks/useResidentMedications'
import { useStockLogs } from '../hooks/useStockLogs'
import { STOCK_LOGS_QUERY_KEY } from '../hooks/queryConstants'
import { getActionTitle, getActionSubtitle } from '../utils/stockRecordUtils'
import { MedicationStockReason, DiscrepancyStatus } from '../types'

const EMARMedicationStockScreenV2: React.FC = () => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const navigate = useNavigate()
  const { id: residentId, medication_id: medicationId } = useParams()
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const [startDate, setStartDate] = useState(dayjs().subtract(14, 'days'))
  const [endDate, setEndDate] = useState(dayjs())

  const { data: medicationsData, isLoading: isLoadingMedication } =
    useResidentMedications({
      homeId: home?.id || '',
      residentId: residentId || '',
      residentMedicationId: medicationId
    })

  const {
    records,
    chartData: stockChartData,
    isLoading: isLoadingLogs
  } = useStockLogs({
    medicationId: medicationId || '',
    startDate,
    endDate,
    includeChartData: true
  })

  const isLoading = isLoadingMedication || isLoadingLogs

  const allMedications = [
    ...(medicationsData?.scheduledMedications || []),
    ...(medicationsData?.prnMedications || []),
    ...(medicationsData?.discontinuedMedications || [])
  ]

  const medicationData = allMedications.find(
    (med) => med.residentMedicationId === medicationId
  )

  const [stockCheckModalOpen, setStockCheckModalOpen] = useState(false)
  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false)
  const [disposalModalOpen, setDisposalModalOpen] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null)
  const [editingRecord, setEditingRecord] = useState<any>(null)
  const [isEditMode, setIsEditMode] = useState(false)

  const logs = records.map((record) => ({
    id: record.id,
    timeLogged: record.date,
    amount1: record.stockCount,
    amount2: record.expectedStock,
    action: record.action,
    actionType: record.action,
    title: getActionTitle(record.action, t),
    subtitle: getActionSubtitle(
      record.action,
      record.quantityChange || 0,
      record.unit || t('TABLETS'),
      record.notes,
      t
    ),
    description: Array.isArray(record.notes)
      ? record.notes.join(', ')
      : record.notes || '',
    hasDiscrepancy: !!record.discrepancy,
    isResolved:
      record.discrepancy?.status === DiscrepancyStatus.RECONCILED || false,
    loggedBy: {
      id: record.recordedBy.id,
      name: record.recordedBy.name || { first: '', last: '' }
    },
    deleted: false
  }))

  const sortedLogs = [...logs].sort(
    (a, b) => dayjs(b.timeLogged).valueOf() - dayjs(a.timeLogged).valueOf()
  )

  if (isLoading || !medicationData) {
    return (
      <PageWrapper>
        <Spinner />
      </PageWrapper>
    )
  }

  const medication = {
    id: medicationData.residentMedicationId,
    name: medicationData.medicationName,
    stockQuantityType: medicationData.unit,
    quantityType: medicationData.unit,
    currentStock: medicationData.currentStockLevel,
    expectedStock: medicationData.currentStockLevel,
    lowStockThreshold: medicationData.stockAlertLevel
  }

  const showAlert = medication.currentStock <= medication.lowStockThreshold

  const chartData = [
    {
      name: 'Stock Count',
      data: stockChartData.map((point) => point.stockLevel)
    }
  ]

  const handleDateChange = (dates: any) => {
    if (dates && dates[0] && dates[1]) {
      setStartDate(dates[0])
      setEndDate(dates[1])
    } else {
      setStartDate(dayjs().subtract(14, 'days'))
      setEndDate(dayjs())
    }
  }

  const handleStockAction = (action: string) => {
    if (action === 'Stock Check') {
      setStockCheckModalOpen(true)
    } else if (action === 'New Delivery Received') {
      setDeliveryModalOpen(true)
    } else if (action === 'Disposal') {
      setDisposalModalOpen(true)
    }
  }

  const handleStockCheckSuccess = () => {
    setStockCheckModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleStockCheckCancel = () => {
    setStockCheckModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleDeliverySuccess = () => {
    setDeliveryModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleDeliveryCancel = () => {
    setDeliveryModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleDisposalSuccess = () => {
    setDisposalModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleDisposalCancel = () => {
    setDisposalModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleDownloadPDF = () => {}

  const handleReadMore = () => {
    window.open(
      'https://support.logmycare.co.uk/en/collections/2957232-stock',
      '_blank',
      'noopener,noreferrer'
    )
  }

  const handleViewRecord = (recordId: string) => {
    setSelectedRecordId(recordId)
    setDrawerOpen(true)
  }

  const handleEditRecord = (recordId: string) => {
    const record = sortedLogs.find((log) => log.id === recordId)
    if (!record) return

    setIsEditMode(true)
    setEditingRecord(record)

    switch (record.action) {
      case MedicationStockReason.STOCK_COUNT:
        setStockCheckModalOpen(true)
        break
      case MedicationStockReason.RESTOCK:
        setDeliveryModalOpen(true)
        break
      case MedicationStockReason.WASTE:
        setDisposalModalOpen(true)
        break
    }
  }

  const handleViewAdministrationRecord = (recordId: string) => {
    setSelectedRecordId(recordId)
    setDrawerOpen(true)
  }

  // const handleViewCommentRecord = (recordId: string) => {
  //   setSelectedRecordId(recordId)
  //   setSelectedRecordType('comment')
  //   setDrawerOpen(true)
  // }

  const handleCloseDrawer = () => {
    setDrawerOpen(false)
    setSelectedRecordId(null)
  }

  const handleEditFromDrawer = (
    record: any,
    actionType: MedicationStockReason
  ) => {
    setIsEditMode(true)
    setEditingRecord(record)

    switch (actionType) {
      case MedicationStockReason.STOCK_COUNT:
        setStockCheckModalOpen(true)
        break
      case MedicationStockReason.RESTOCK:
        setDeliveryModalOpen(true)
        break
      case MedicationStockReason.WASTE:
        setDisposalModalOpen(true)
        break
      default:
        break
    }
  }

  const getStockCheckInitialData = (record: any) => {
    if (!record) return undefined
    return {
      recordId: record.id,
      description: record.description || record.title || '',
      stockCount: record.amount1,
      adjustment: record.amount1 - record.amount2,
      additionalDetails: false,
      isExpectedStockAccurate: record.amount1 === record.amount2
    }
  }

  const getDeliveryInitialData = (record: any) => {
    if (!record) return undefined
    const receivedBy = record.receivedBy || record.loggedBy

    return {
      recordId: record.id,
      description: record.description || record.title || '',
      receivedStock: record.receivedStock || record.amount1 - record.amount2,
      newStock: record.amount1,
      isStockAccurate: false,
      resolvedBy: receivedBy?.id || '',
      receiveDate: record.receivedDate || record.timeLogged,
      resolveDiscrepancy: null,
      discrepancyReason: '',
      receivedFrom: record.receivedFrom || '',
      witnessedBy: '',
      witnessPin: '',
      additionalDetails: false
    }
  }

  const getDisposalInitialData = (record: any) => {
    if (!record) return undefined
    const actionedBy = record.actionedBy || record.loggedBy
    return {
      recordId: record.id,
      description: record.description || record.title || '',
      disposedStock: record.disposedStock || record.amount2 - record.amount1,
      newStock: record.amount1,
      hasDiscrepancy: record.hasDiscrepancy || false,
      discrepancyReason: record.discrepancyReason || '',
      actionedBy: actionedBy?.id || '',
      actionedDate: record.actionedDate || record.timeLogged,
      disposalMethod: record.disposalMethod || '',
      additionalDetails: false
    }
  }

  const selectedRecord = sortedLogs.find((log) => log.id === selectedRecordId)

  const stockMenuItems = [
    {
      key: 'stock-check',
      label: t('STOCK_CHECK'),
      onClick: () => handleStockAction('Stock Check')
    },
    {
      key: 'new-delivery',
      label: t('NEW_DELIVERY_RECEIVED'),
      onClick: () => handleStockAction('New Delivery Received')
    },
    {
      key: 'disposal',
      label: t('DISPOSAL'),
      onClick: () => handleStockAction('Disposal')
    }
  ]

  const handleBreadcrumbClick = () => {
    navigate(`/residents/${residentId}/emar/stock`)
  }

  return (
    <>
      <PageHeader
        sticky
        title={t('STOCK_MEDICATION_NAME', { medicationName: medication.name })}
        breadcrumb={{
          text: t('STOCK'),
          onClick: handleBreadcrumbClick
        }}
        testId="med-stock-header-v2"
      >
        <HeaderActions>
          <ReadMoreLink
            onClick={handleReadMore}
            data-testid="read-more-link-v2"
          >
            {t('READ_MORE')} <ExternalLink />
          </ReadMoreLink>

          <DownloadButton
            icon={<DownloadOutlined />}
            onClick={handleDownloadPDF}
            data-testid="download-pdf-btn-v2"
          >
            {t('DOWNLOAD_PDF')}
          </DownloadButton>

          <Dropdown
            menu={{ items: stockMenuItems }}
            placement="bottomRight"
            trigger={['click']}
          >
            <Button variant="primary" data-testid="manage-stock-btn-v2">
              {t('MANAGE_STOCK')}
              <Chevron direction="down" />
            </Button>
          </Dropdown>
        </HeaderActions>
      </PageHeader>
      <PageWrapper testId="emar-medication-stock-screen-v2">
        {showAlert && (
          <Banner
            color={colors.red}
            text={t('LOW_STOCK_ALERT')}
            data-testid="emar-stock-alert-banner-v2"
          />
        )}

        <FilterContainer title="">
          <RangePicker
            defaultValue={[startDate, endDate]}
            onChange={handleDateChange}
            data-testid="date-range-picker-v2"
          />
        </FilterContainer>

        <Card title={t('STOCK_LEVEL_CHART')}>
          <LineChart
            data={chartData}
            xAxisLabel={t('DATE')}
            yAxisLabel={t('STOCK_COUNT_UNIT', {
              unit: medication.stockQuantityType
            })}
            startDate={startDate.toISOString()}
            endDate={endDate.toISOString()}
            dateFrame="week"
          />
        </Card>

        <StockRecordsTable
          logs={sortedLogs}
          onViewRecord={handleViewRecord}
          onEditRecord={handleEditRecord}
          onViewAdministrationRecord={handleViewAdministrationRecord}
          // onViewCommentRecord={handleViewCommentRecord}
        />

        <StockCheckModal
          open={stockCheckModalOpen}
          medicationName={medication.name}
          expectedStock={medication.expectedStock}
          residentId={residentId || ''}
          residentMedicationId={medicationId || ''}
          onCancel={handleStockCheckCancel}
          onSuccess={handleStockCheckSuccess}
          editMode={isEditMode}
          initialData={
            isEditMode ? getStockCheckInitialData(editingRecord) : undefined
          }
        />
        <DeliveryModal
          open={deliveryModalOpen}
          medicationName={medication.name}
          expectedStock={medication.expectedStock}
          residentId={residentId || ''}
          residentMedicationId={medicationId || ''}
          onCancel={handleDeliveryCancel}
          onSuccess={handleDeliverySuccess}
          editMode={isEditMode}
          initialData={
            isEditMode ? getDeliveryInitialData(editingRecord) : undefined
          }
        />
        <DisposalModal
          open={disposalModalOpen}
          medicationName={medication.name}
          expectedStock={medication.expectedStock}
          residentId={residentId || ''}
          residentMedicationId={medicationId || ''}
          onCancel={handleDisposalCancel}
          onSuccess={handleDisposalSuccess}
          editMode={isEditMode}
          initialData={
            isEditMode ? getDisposalInitialData(editingRecord) : undefined
          }
        />
        <StockRecordDrawer
          isVisible={drawerOpen}
          onClose={handleCloseDrawer}
          record={selectedRecord}
          medicationName={medication.name}
          onEdit={handleEditFromDrawer}
        />
      </PageWrapper>
    </>
  )
}

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const ReadMoreLink = styled.a`
  color: ${colors.primary};
  text-decoration: none;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: color 0.2s ease;

  &:hover {
    color: ${colors.primary};
    text-decoration: underline;
    opacity: 0.8;
  }
`

const DownloadButton = styled(Button)`
  white-space: nowrap;

  ${({ theme }) => theme.mediaQueries.tabletDown} {
    display: none;
  }
`

export default EMARMedicationStockScreenV2
